import {testIdProps} from '@github-ui/test-id-props'
import {LockIcon, PencilIcon} from '@primer/octicons-react'
import {Button, ToggleSwitch, useConfirm} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {type PropsWithChildren, useCallback, useEffect, useRef} from 'react'

import {AutoAssignWorkflowToggleName, AutoAssignWorkflowToggleUI} from '../../api/stats/contracts'
import type {ClientMemexWorkflow} from '../../api/workflows/contracts'
import {isAutoAssignAgentWorkflow, isWorkflowPersisted} from '../../helpers/workflow-utilities'
import {usePostStats} from '../../hooks/common/use-post-stats'
import {usePreviousValue} from '../../hooks/common/use-previous-value'
import {AutomationGraphStateProvider} from '../../state-providers/workflows/automation-graph-state-provider'
import {useAutomationGraph} from '../../state-providers/workflows/use-automation-graph'
import {useWorkflows} from '../../state-providers/workflows/use-workflows'
import {WorkflowResources} from '../../strings'
import {BlankslateErrorMessage} from '../error-boundaries/blankslate-error-message'
import {ErrorBoundary} from '../error-boundaries/error-boundary'
import {AutomationGraph} from './automation-graph'
import styles from './automation-graph-view.module.css'
import {WorkflowName} from './workflow-name'

type AutomationGraphViewProps = {
  startInEditMode: boolean
  workflow: ClientMemexWorkflow
  onNoSuggestedRepositories?: () => void
}

const ReadOnlyLabel = () => {
  return (
    <div className={styles.readOnlyLabel} {...testIdProps('read-only-label')}>
      <Octicon icon={LockIcon} size={12} className={styles.readOnlyIcon} />
      <span>{WorkflowResources.readOnlyLabel}</span>
    </div>
  )
}
const DiscardButton = ({ref}: {ref?: React.Ref<HTMLButtonElement>}) => {
  const {discard} = useAutomationGraph()

  return (
    <Button onClick={discard} {...testIdProps('workflow-discard-button')} className={styles.workflowButton} ref={ref}>
      {WorkflowResources.discardWorkflow}
    </Button>
  )
}

DiscardButton.displayName = 'DiscardButton'

const DeleteButton = ({clientId}: {clientId: string}) => {
  const {deleteNonPersistedWorkflow} = useWorkflows()
  const confirm = useConfirm()

  const onDelete = useCallback(async () => {
    const shouldDelete = await confirm({
      title: WorkflowResources.deleteWorkflowConfirmationTitle,
      content: WorkflowResources.deleteWorkflowConfirmationContent,
      confirmButtonContent: WorkflowResources.deleteWorkflowConfirmationButton,
      confirmButtonType: 'danger',
    })

    if (shouldDelete) {
      deleteNonPersistedWorkflow(clientId)
    }
  }, [confirm, deleteNonPersistedWorkflow, clientId])

  return (
    <Button onClick={() => onDelete()} {...testIdProps('workflow-delete-button')} className={styles.workflowButton}>
      {WorkflowResources.deleteWorkflow}
    </Button>
  )
}

const AutomationGraphViewContent = ({children}: PropsWithChildren) => {
  const {isEditing, edit, workflow, save, isWorkflowValid} = useAutomationGraph()
  const {postStats} = usePostStats()

  const {shouldDisableWorkflowToggle} = useAutomationGraph()

  const toggleOnClick = useCallback(async () => {
    const newEnabled = !workflow.enabled
    if (isAutoAssignAgentWorkflow(workflow)) {
      postStats({
        name: AutoAssignWorkflowToggleName,
        ui: AutoAssignWorkflowToggleUI.WorkflowEditor,
        context: newEnabled ? 'enable' : 'disable',
      })
    }
    save(newEnabled)
  }, [postStats, save, workflow])

  const isPersisted = isWorkflowPersisted(workflow)
  const discardButtonRef = useRef<HTMLButtonElement>(null)
  const editButtonRef = useRef<HTMLButtonElement>(null)
  const isEditingPrev = usePreviousValue(isEditing)

  useEffect(() => {
    // skip when there aren't any changes
    if (isEditing === isEditingPrev) {
      return
    }

    if (isEditing) {
      // if we were not previously editing and now we are, return focus to the discard button
      discardButtonRef.current?.focus()
    } else {
      // if we were previously editing and now we're not, return focus to the edit button
      editButtonRef.current?.focus()
    }
  }, [isEditing, isEditingPrev])

  return (
    <div className={styles.graphContainer}>
      <div className={styles.graphHeader}>
        <WorkflowName />
        <div className={styles.actionButtonsContainer}>
          {!isEditing && (
            <>
              {isPersisted && (
                <span {...testIdProps('workflow-enable-toggle-container')}>
                  <ToggleSwitch
                    checked={workflow.enabled}
                    onClick={toggleOnClick}
                    disabled={shouldDisableWorkflowToggle}
                    aria-labelledby="workflow-name"
                  />
                </span>
              )}
              <Button
                ref={editButtonRef}
                leadingVisual={PencilIcon}
                onClick={edit}
                className={styles.workflowButton}
                {...testIdProps('workflow-edit-button')}
              >
                {WorkflowResources.editWorkflow}
              </Button>
            </>
          )}
          {isEditing && (
            <>
              {!isPersisted && workflow.isUserWorkflow ? (
                <DeleteButton clientId={workflow.clientId} />
              ) : (
                <DiscardButton ref={discardButtonRef} />
              )}
              <Button
                variant="primary"
                disabled={!isWorkflowValid}
                onClick={() => {
                  save()
                }}
                className={styles.workflowButton}
                {...testIdProps('workflow-save-button')}
              >
                {workflow.enabled ? WorkflowResources.saveWorkflowWithoutEnable : WorkflowResources.saveWorkflow}
              </Button>
            </>
          )}
        </div>
      </div>
      <div className={styles.mainContentArea}>
        {!isEditing && <ReadOnlyLabel />}
        {children}
      </div>
    </div>
  )
}

export const AutomationGraphView = ({
  workflow,
  onNoSuggestedRepositories,
  startInEditMode,
}: AutomationGraphViewProps) => (
  <AutomationGraphStateProvider startInEditMode={startInEditMode} initialWorkflow={workflow}>
    <ErrorBoundary
      fallback={
        <BlankslateErrorMessage heading="Oops!" content="Something went wrong loading this workflow configuration." />
      }
    >
      <AutomationGraphViewContent>
        <AutomationGraph onNoSuggestedRepositories={onNoSuggestedRepositories} />
      </AutomationGraphViewContent>
    </ErrorBoundary>
  </AutomationGraphStateProvider>
)
